import React, { useState, useEffect, useCallback, useRef } from "react";
import {
	startBroadcast,
	getBroadcastsList,
	resumeBroadcast,
	cancelBroadcast,
} from "../../api/apiServiceFetch";
import { DataGrid } from "@mui/x-data-grid";
import {
	Box,
	Typography,
	Button,
	TextField,
	LinearProgress,
} from "@mui/material";
import resizeImage from "../../components/ResizeImage";
import ConfirmPopup from "../../components/ConfirmPopup";

const mediaLabels = {
	0: "Текст",
	1: "Фото",
	2: "Видео",
	3: "Голос",
	4: "Аудио",
};

const statusLabels = {
	pending: "ожидает",
	in_progress: "выполняется",
	completed: "завершена",
	aborted: "прервана",
};

const statusColors = {
	pending: "#888",
	in_progress: "#0a84ff",
	completed: "#22c55e",
	aborted: "#ef4444",
};

const readFileAsBase64 = (file) =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result.split(",")[1]);
		reader.onerror = reject;
		reader.readAsDataURL(file);
	});

const Broadcasts = () => {
	const [jobs, setJobs] = useState([]);
	const [message, setMessage] = useState("");
	const [media, setMedia] = useState(null);
	const [mediaType, setMediaType] = useState(null);
	const [sending, setSending] = useState(false);
	const [info, setInfo] = useState("");
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [busyId, setBusyId] = useState(null);
	const fileRef = useRef(null);

	const load = useCallback(async () => {
		try {
			const data = await getBroadcastsList(100, 0);
			setJobs(Array.isArray(data) ? data : []);
		} catch (e) {
			console.error("getBroadcastsList failed:", e);
		}
	}, []);

	// первичная загрузка + автообновление каждые 3 секунды (живой прогресс)
	useEffect(() => {
		load();
		const id = setInterval(load, 3000);
		return () => clearInterval(id);
	}, [load]);

	const handleMediaChange = (event) => {
		const file = event.target.files[0];
		if (!file) {
			setMedia(null);
			setMediaType(null);
			return;
		}
		if (file.type.startsWith("image/")) {
			setMediaType("photo");
			setMedia(file);
		} else if (file.type.startsWith("video/")) {
			setMediaType("video");
			setMedia(file);
		} else {
			setInfo("Неподдерживаемый тип файла");
		}
	};

	const doSend = async () => {
		setConfirmOpen(false);
		setSending(true);
		setInfo("");

		let base64Media = null;
		try {
			if (media) {
				if (mediaType === "photo") {
					const { base64String } = await resizeImage(media, 800, 800);
					base64Media = base64String;
				} else if (mediaType === "video") {
					base64Media = await readFileAsBase64(media);
				}
			}

			const res = await startBroadcast({
				message,
				base64Media,
				mediaType,
			});

			setInfo(res?.message || "Рассылка запущена");
			setMessage("");
			setMedia(null);
			setMediaType(null);
			if (fileRef.current) fileRef.current.value = "";
			await load();
		} catch (e) {
			setInfo("Ошибка: " + (e?.message || e));
		} finally {
			setSending(false);
		}
	};

	const handleResume = async (id) => {
		setBusyId(id);
		try {
			await resumeBroadcast(id);
			setInfo(`Возобновление рассылки #${id} запланировано`);
			setTimeout(load, 1200);
		} catch (e) {
			setInfo("Ошибка: " + (e?.message || e));
		} finally {
			setBusyId(null);
		}
	};

	const handleCancel = async (id) => {
		if (!window.confirm(`Отменить рассылку #${id}?`)) return;
		setBusyId(id);
		try {
			await cancelBroadcast(id);
			setTimeout(load, 800);
		} catch (e) {
			setInfo("Ошибка: " + (e?.message || e));
		} finally {
			setBusyId(null);
		}
	};

	const formatDate = (d) => {
		if (!d) return "-";
		const date = new Date(d);
		return `${String(date.getDate()).padStart(2, "0")}.${String(
			date.getMonth() + 1,
		).padStart(2, "0")} ${String(date.getHours()).padStart(2, "0")}:${String(
			date.getMinutes(),
		).padStart(2, "0")}`;
	};

	const columns = [
		{ field: "id", headerName: "ID", width: 70 },
		{
			field: "media",
			headerName: "Медиа",
			width: 90,
			renderCell: (c) => mediaLabels[c.row.media] ?? c.row.media,
		},
		{
			field: "status",
			headerName: "Статус",
			width: 120,
			renderCell: (c) => {
				const st =
					c.row.status !== "aborted" && c.row.pending === 0
						? "completed"
						: c.row.status;
				return (
					<span style={{ color: statusColors[st] ?? "#000", fontWeight: 600 }}>
						{statusLabels[st] ?? st}
					</span>
				);
			},
		},
		{
			field: "progress",
			headerName: "Прогресс",
			width: 160,
			renderCell: (c) => {
				const total = c.row.totalUsers || 0;
				const done =
					(c.row.success || 0) + (c.row.failed || 0) + (c.row.deleted || 0);
				const pct = total > 0 ? Math.round((done / total) * 100) : 0;
				return (
					<Box sx={{ width: "100%" }}>
						<Typography variant="caption">
							{done}/{total} ({pct}%)
						</Typography>
						<LinearProgress variant="determinate" value={pct} />
					</Box>
				);
			},
		},
		{ field: "totalUsers", headerName: "Всего", width: 80 },
		{ field: "success", headerName: "✅", width: 70 },
		{ field: "failed", headerName: "❌", width: 70 },
		{ field: "deleted", headerName: "🗑️", width: 70 },
		{ field: "pending", headerName: "⏳", width: 70 },
		{
			field: "createdAt",
			headerName: "Создана",
			width: 120,
			renderCell: (c) => formatDate(c.row.createdAt),
		},
		{
			field: "messagePreview",
			headerName: "Сообщение",
			width: 220,
			renderCell: (c) => c.row.messagePreview || "-",
		},
		{
			field: "actions",
			headerName: "Действие",
			width: 230,
			renderCell: (c) => {
				const canResume = c.row.pending > 0 && c.row.status !== "completed";
				const canCancel = c.row.status === "in_progress";
				return (
					<Box display="flex" gap={1}>
						{canResume && (
							<Button
								size="small"
								variant="contained"
								color="primary"
								disabled={busyId === c.row.id}
								onClick={() => handleResume(c.row.id)}
								sx={{ "&:focus": { outline: "none" } }}
							>
								Возобновить
							</Button>
						)}
						{canCancel && (
							<Button
								size="small"
								variant="outlined"
								color="error"
								disabled={busyId === c.row.id}
								onClick={() => handleCancel(c.row.id)}
								sx={{ "&:focus": { outline: "none" } }}
							>
								Отменить
							</Button>
						)}
					</Box>
				);
			},
		},
	];

	return (
		<Box p={3}>
			<ConfirmPopup
				isOpen={confirmOpen}
				onConfirm={doSend}
				onCancel={() => setConfirmOpen(false)}
				question={"Отправить рассылку всем пользователям?"}
			/>

			<Typography variant="h6" mb={2}>
				Рассылка
			</Typography>

			<Box
				sx={{
					maxWidth: 640,
					mb: 3,
					p: 2,
					border: "1px solid #e0e0e0",
					borderRadius: 2,
				}}
			>
				<TextField
					label="Текст сообщения"
					value={message}
					onChange={(e) => setMessage(e.target.value)}
					fullWidth
					multiline
					minRows={3}
					disabled={sending}
					sx={{ mb: 2 }}
				/>

				<input
					ref={fileRef}
					type="file"
					accept="image/*,video/*"
					onChange={handleMediaChange}
					disabled={sending}
				/>

				<Box mt={2} display="flex" alignItems="center" gap={2}>
					<Button
						variant="contained"
						color="success"
						disabled={sending || (!message.trim() && !media)}
						onClick={() => setConfirmOpen(true)}
					>
						Отправить
					</Button>
					{sending && <LinearProgress sx={{ flex: 1 }} />}
				</Box>

				{info && (
					<Typography sx={{ mt: 2, color: "#1976d2" }}>{info}</Typography>
				)}
			</Box>

			<Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
				<Typography variant="subtitle1">История рассылок</Typography>
				<Button size="small" onClick={load}>
					Обновить
				</Button>
			</Box>

			<DataGrid
				rows={jobs}
				density="compact"
				getRowId={(row) => row.id}
				hideFooterSelectedRowCount
				columnHeaderHeight={40}
				disableColumnSelector
				autoHeight
				pageSizeOptions={[10, 25, 50]}
				initialState={{
					pagination: { paginationModel: { pageSize: 10, page: 0 } },
				}}
				sx={{
					"& .MuiDataGrid-cell:focus": { outline: "none" },
					"& .MuiDataGrid-columnHeader:focus": { outline: "none" },
				}}
				columns={columns}
			/>
		</Box>
	);
};

export default Broadcasts;
